/**
 * Lab Queue Page - Clara Vale V2
 *
 * Decision-first design: Lab techs see what needs attention at a glance,
 * grouped by defect type so internal assignment is fast. Matches the
 * Registrations/Claims list page structure exactly.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { User, ChevronRight, Download } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import { AppShell } from '@/admin/components/app-shell'
import { DataTable, type DataTableColumn, ColumnToggle } from '@/admin/components/data-table'
import { StatusPills, type StatusPillItem } from '@/admin/components/shared/status-pills'
import { ListPageHeader } from '@/admin/components/shared/list-page-header'
import { useLabQueue, useAssignLabTask, type LabQueueItem } from '@/admin/hooks/use-lab-tasks'
import { formatClaimId } from '@/admin/lib/formatting'

const TABLE_ID = 'lab-queue'

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  REQUESTED: { label: 'Unassigned', variant: 'outline' },
  ASSIGNED: { label: 'Assigned', variant: 'secondary' },
  IN_PROGRESS: { label: 'In Progress', variant: 'default' },
  COMPLETE: { label: 'Complete', variant: 'secondary' },
  CANCELLED: { label: 'Cancelled', variant: 'destructive' },
}

const PRIORITY_COLORS: Record<string, string> = {
  HIGH: 'text-[var(--red)]',
  STANDARD: 'text-[var(--ink-2)]',
  LOW: 'text-[var(--ink-3)]',
}

export function LabQueuePage() {
  const navigate = useNavigate()
  const { data: queue = [], isLoading, isFetching, isError, refetch } = useLabQueue()
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})

  const statusPills: StatusPillItem[] = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const item of queue) {
      const status = item.lab_task_status ?? 'REQUESTED'
      counts[status] = (counts[status] ?? 0) + 1
    }
    const pills: StatusPillItem[] = []
    if (counts['REQUESTED']) pills.push({ key: 'REQUESTED', label: 'Unassigned', count: counts['REQUESTED'] })
    if (counts['ASSIGNED']) pills.push({ key: 'ASSIGNED', label: 'Assigned', count: counts['ASSIGNED'] })
    if (counts['IN_PROGRESS']) pills.push({ key: 'IN_PROGRESS', label: 'In Progress', count: counts['IN_PROGRESS'] })
    if (counts['COMPLETE']) pills.push({ key: 'COMPLETE', label: 'Complete', count: counts['COMPLETE'] })
    return pills
  }, [queue])

  const filteredQueue = useMemo(() => {
    let items = queue
    if (statusFilter) {
      items = items.filter((i) => (i.lab_task_status ?? 'REQUESTED') === statusFilter)
    }
    if (search) {
      const q = search.toLowerCase()
      items = items.filter(
        (i) =>
          (i.claim_ref ?? '').toLowerCase().includes(q) ||
          (i.defect_type ?? '').toLowerCase().includes(q) ||
          (i.product_category ?? '').toLowerCase().includes(q) ||
          (i.assigned_to_name ?? '').toLowerCase().includes(q),
      )
    }
    return items
  }, [queue, statusFilter, search])

  const unassignedCount = queue.filter((i) => !i.assigned_to_name).length

  const columns: DataTableColumn<LabQueueItem>[] = [
    {
      id: 'claim_ref',
      header: 'Claim',
      cell: (item) => (
        <div>
          <p className="text-[12px] font-medium text-[var(--ink)]">
            {item.claim_ref || formatClaimId(item.claim_uuid)}
          </p>
          {item.product_category && (
            <p className="text-[10px] text-[var(--ink-3)]">{item.product_category}</p>
          )}
        </div>
      ),
    },
    {
      id: 'defect_type',
      header: 'Defect Type',
      cell: (item) => (
        <span className="text-[12px] text-[var(--ink-2)]">
          {item.defect_type
            ? item.defect_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
            : '-'}
        </span>
      ),
    },
    {
      id: 'priority',
      header: 'Priority',
      cell: (item) => (
        <span className={cn('text-[12px] font-medium', PRIORITY_COLORS[item.priority ?? 'STANDARD'])}>
          {item.priority ?? 'Standard'}
        </span>
      ),
    },
    {
      id: 'lab_task_status',
      header: 'Status',
      cell: (item) => {
        const status = item.lab_task_status ?? 'REQUESTED'
        const badge = STATUS_BADGES[status] ?? STATUS_BADGES.REQUESTED
        return <Badge variant={badge.variant}>{badge.label}</Badge>
      },
    },
    {
      id: 'assigned_to_name',
      header: 'Technician',
      cell: (item) =>
        item.assigned_to_name ? (
          <span className="flex items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
            <User className="h-3 w-3" />
            {item.assigned_to_name}
          </span>
        ) : (
          <AssignToMeButton
            claimUuid={item.claim_uuid}
            taskUuid={item.lab_task_uuid}
          />
        ),
    },
    {
      id: 'days_in_lab',
      header: 'Days in Lab',
      cell: (item) => (
        <span className="text-[12px] tabular-nums text-[var(--ink-3)]">
          {item.days_in_lab ?? 0}d
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: () => (
        <ChevronRight className="h-3.5 w-3.5 text-[var(--ink-3)]" />
      ),
    },
  ]

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* ── Page Header (clara-title + subtitle) ─────────── */}
        <ListPageHeader
          title="Lab"
          totalCount={queue.length}
          highlightStat={
            unassignedCount > 0
              ? { label: 'awaiting assignment', value: unassignedCount }
              : undefined
          }
          timeFilterValue="all_time"
          onTimeFilterChange={() => {}}
        />

        {/* ── Toolbar: Status pills | Search + Columns + Export ── */}
        <div className="cv-data-table relative z-10 mb-5 flex flex-wrap items-center justify-between gap-2 md:gap-3">
          {/* Left: Status pills */}
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            <StatusPills
              items={statusPills}
              activeKey={statusFilter}
              onSelect={setStatusFilter}
              totalCount={queue.length}
            />
          </div>

          {/* Right: Search + Columns + Export */}
          <div className="flex items-center gap-2">
            <ColumnToggle
              columns={columns}
              visibility={columnVisibility}
              onVisibilityChange={setColumnVisibility}
            />
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search claims..."
              className="w-[200px]"
            />
            <Button
              variant="outline"
              size="icon-sm"
              className="shrink-0"
              disabled
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* ── Queue Table ──────────────────────────────────── */}
        <DataTable
          tableId={TABLE_ID}
          columns={columns}
          data={filteredQueue}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={refetch}
          columnVisibility={columnVisibility}
          rowKey={(item) => item.claim_uuid}
          onRowClick={(item) => navigate(`/claims/${item.claim_uuid}`)}
          emptyMessage={
            isLoading
              ? 'Loading lab queue…'
              : search
                ? 'No claims match your search'
                : statusFilter
                  ? `No ${STATUS_BADGES[statusFilter]?.label.toLowerCase() ?? ''} claims`
                  : 'No claims in the lab queue'
          }
        />
      </main>
    </AppShell>
  )
}

function AssignToMeButton({
  claimUuid,
  taskUuid,
}: {
  claimUuid: string
  taskUuid: string | null
}) {
  const assign = useAssignLabTask(claimUuid, taskUuid ?? '')

  if (!taskUuid) return <span className="text-[11px] text-[var(--ink-3)]">No task</span>

  return (
    <Button
      variant="outline"
      size="xs"
      disabled={assign.isPending}
      onClick={(e) => {
        e.stopPropagation()
        assign.mutate()
      }}
      className="text-[10px] uppercase tracking-[0.08em]"
    >
      {assign.isPending ? 'Assigning…' : 'Assign to Me'}
    </Button>
  )
}
